import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { templatesApi, entitiesApi } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Sparkles } from "lucide-react";

/**
 * Two-step launcher:
 *   step 1: configure (entity, name, start_date, priority, budget, currency, owner)
 *   step 2: preview generated rows → user confirms → we instantiate.
 * Nothing is written until the confirm button on step 2.
 */
export default function NewFromTemplateDialog({ open, setOpen, template, defaultEntityId, onCreated }) {
  const [step, setStep] = useState("configure");
  const [entities, setEntities] = useState([]);
  const [form, setForm] = useState({
    entity_id: "", name: "", start_date: new Date().toISOString().slice(0, 10),
    target_completion: "", budget: "", currency: "USD", priority: "", owner_contact_id: "",
  });
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !template) return;
    setStep("configure");
    setPreview(null);
    setForm(f => ({
      ...f,
      entity_id: defaultEntityId || "",
      name: template?.name || "",
      currency: template?.default_currency || "USD",
      priority: template?.default_priority || "",
      start_date: new Date().toISOString().slice(0, 10),
      target_completion: "", budget: "", owner_contact_id: "",
    }));
    entitiesApi.list().then(r => setEntities(r.data || [])).catch(() => {});
  }, [open, template, defaultEntityId]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const entityOptions = useMemo(() => entities.filter(e => !e.is_archived), [entities]);

  const doPreview = async () => {
    if (!form.entity_id) return toast.error("Choose an entity");
    if (!form.name.trim()) return toast.error("Project name is required");
    if (!form.start_date) return toast.error("Start date is required");
    try {
      const r = await templatesApi.preview(template.id, {
        entity_id: form.entity_id,
        name: form.name.trim(),
        start_date: form.start_date,
        target_completion: form.target_completion || null,
        budget: form.budget ? Number(form.budget) : 0,
        currency: form.currency || null,
        priority: form.priority || null,
        owner_contact_id: form.owner_contact_id || null,
      });
      setPreview(r.data);
      setStep("preview");
    } catch (e) { toast.error(e.response?.data?.detail || "Preview failed"); }
  };

  const confirm = async () => {
    setSaving(true);
    try {
      const r = await templatesApi.instantiate(template.id, {
        entity_id: form.entity_id,
        name: form.name.trim(),
        start_date: form.start_date,
        target_completion: form.target_completion || null,
        budget: form.budget ? Number(form.budget) : 0,
        currency: form.currency || null,
        priority: form.priority || null,
        owner_contact_id: form.owner_contact_id || null,
      });
      toast.success(`Created ${r.data.tasks.length} tasks, ${r.data.events.length} milestones`);
      onCreated?.(r.data);
      setOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Create failed"); }
    finally { setSaving(false); }
  };

  if (!template) return null;
  const L = "text-white/60 text-xs uppercase tracking-wider";
  const I = "mt-1.5 bg-white/5 border-white/10";

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-[#151A23] border-white/10 text-white max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="new-from-template-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-white/70"/> Launch from "{template.name}"
          </DialogTitle>
        </DialogHeader>

        {step === "configure" && (
          <div className="space-y-4 py-2" data-testid="nft-configure-step">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className={L}>Entity</Label>
                <Select value={form.entity_id || undefined} onValueChange={v => set("entity_id", v)}>
                  <SelectTrigger className={I} data-testid="nft-entity"><SelectValue placeholder="Choose an entity"/></SelectTrigger>
                  <SelectContent className="bg-[#151A23] border-white/10 text-white">
                    {entityOptions.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label className={L}>Project name</Label><Input value={form.name} onChange={e=>set("name", e.target.value)} className={I} data-testid="nft-name"/></div>
              <div><Label className={L}>Start date</Label><Input type="date" value={form.start_date} onChange={e=>set("start_date", e.target.value)} className={I} data-testid="nft-start"/></div>
              <div><Label className={L}>Target completion (optional)</Label><Input type="date" value={form.target_completion} onChange={e=>set("target_completion", e.target.value)} className={I}/></div>
              <div>
                <Label className={L}>Priority (optional override)</Label>
                <Select value={form.priority || "__use_template"} onValueChange={v => set("priority", v === "__use_template" ? "" : v)}>
                  <SelectTrigger className={I}><SelectValue/></SelectTrigger>
                  <SelectContent className="bg-[#151A23] border-white/10 text-white">
                    <SelectItem value="__use_template">Use template default</SelectItem>
                    {["Low","Medium","High","Critical"].map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label className={L}>Budget (optional)</Label><Input type="number" min="0" value={form.budget} onChange={e=>set("budget", e.target.value)} className={I}/></div>
              <div><Label className={L}>Currency</Label><Input value={form.currency} onChange={e=>set("currency", e.target.value.toUpperCase())} className={I}/></div>
            </div>
            <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-0">
              <Button variant="ghost" onClick={() => setOpen(false)} className="text-white/70 hover:text-white hover:bg-white/10 w-full sm:w-auto">Cancel</Button>
              <Button onClick={doPreview} data-testid="nft-preview-btn" className="rounded-full bg-white text-black hover:bg-white/90 w-full sm:w-auto"><Sparkles className="w-4 h-4 mr-2"/>Preview</Button>
            </DialogFooter>
          </div>
        )}

        {step === "preview" && preview && (
          <div className="space-y-4 py-2" data-testid="nft-preview-step">
            <button onClick={() => setStep("configure")} className="text-xs text-white/60 hover:text-white flex items-center gap-1"><ArrowLeft className="w-3.5 h-3.5"/> Back</button>
            <div className="p-4 rounded-lg border border-white/10 bg-white/[0.02]">
              <div className="text-eyebrow mb-2">Project</div>
              <div className="text-lg font-display font-semibold">{preview.project.name}</div>
              <div className="text-xs text-white/60 mt-1">{preview.project.start_date}{preview.project.target_completion ? ` → ${preview.project.target_completion}` : ""}</div>
              {preview.project.mission && <div className="text-xs text-white/70 mt-2"><span className="text-white/40">Mission:</span> {preview.project.mission}</div>}
              {preview.project.goals && <div className="text-xs text-white/70 mt-1"><span className="text-white/40">Goals:</span> {preview.project.goals}</div>}
            </div>
            <PreviewList title={`Tasks (${preview.tasks.length})`} items={preview.tasks} render={(t) => (
              <div className="flex items-center justify-between gap-2 py-1 text-xs">
                <div className="min-w-0"><div className="text-white/90 truncate">{t.name}</div><div className="text-white/40">{t.priority}</div></div>
                <div className="text-white/50 tabular-nums flex-shrink-0">{t.due_date || "—"}</div>
              </div>
            )} testid="nft-preview-tasks"/>
            <PreviewList title={`Milestones (${preview.events.length})`} items={preview.events} render={(e) => (
              <div className="flex items-center justify-between gap-2 py-1 text-xs">
                <div className="min-w-0"><div className="text-white/90 truncate">{e.title}</div><div className="text-white/40">{e.event_type}</div></div>
                <div className="text-white/50 tabular-nums flex-shrink-0">{(e.starts_at || "").slice(0,10)}</div>
              </div>
            )} testid="nft-preview-events"/>
            {preview.notes.length > 0 && (
              <PreviewList title={`Notes (${preview.notes.length})`} items={preview.notes} render={(n) => (
                <div className="text-xs py-1"><div className="text-white/90">{n.title}</div>{n.content && <div className="text-white/60">{n.content}</div>}</div>
              )} testid="nft-preview-notes"/>
            )}
            <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-0">
              <Button variant="ghost" onClick={() => setOpen(false)} className="text-white/70 hover:text-white hover:bg-white/10 w-full sm:w-auto">Cancel</Button>
              <Button onClick={confirm} disabled={saving} data-testid="nft-confirm-btn" className="rounded-full bg-white text-black hover:bg-white/90 w-full sm:w-auto">
                {saving ? "Creating…" : "Confirm & create project"}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function PreviewList({ title, items, render, testid }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="p-4 rounded-lg border border-white/10 bg-white/[0.02]" data-testid={testid}>
      <div className="text-eyebrow mb-2">{title}</div>
      <div className="divide-y divide-white/5">
        {items.map((row, i) => <div key={i}>{render(row)}</div>)}
      </div>
    </div>
  );
}
